import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-[#F9F6F0] pt-24 noise-overlay" data-testid="privacy-policy-page">
            <section className="section-padding">
                <div className="container-wide mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto"
                    >
                        <span className="inline-block px-4 py-1.5 bg-[#C8A86B]/10 text-[#C8A86B] text-sm font-medium tracking-widest-custom uppercase rounded-full mb-6">
                            Legal
                        </span>
                        <h1 className="font-heading text-4xl sm:text-5xl text-[#4A4E4D] mb-8">
                            Privacy Policy
                        </h1>
                        <p className="text-[#4A4E4D]/60 mb-8">
                            Last updated: February 17, 2026
                        </p>

                        <div className="prose prose-lg max-w-none space-y-8">
                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">1. Introduction</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    Welcome to SAPA ("we," "our," or "us"). SAPA is Bangalore's premier spa directory platform, 
                                    connecting wellness seekers with quality spa and wellness establishments across the city. 
                                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                                    when you visit our website or use our spa directory services.
                                </p>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">2. Information We Collect</h2>
                                <h3 className="font-heading text-xl text-[#4A4E4D] mb-3 mt-6">2.1 Personal Information</h3>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    When you use our spa directory services, we may collect:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li>Name and contact information (email, phone number) when you submit inquiries</li>
                                    <li>Spa preferences and search history to personalize recommendations</li>
                                    <li>Favorite spa selections saved to your account</li>
                                    <li>Location data to show nearby spas in Bangalore</li>
                                    <li>Feedback and reviews you submit about spa experiences</li>
                                </ul>

                                <h3 className="font-heading text-xl text-[#4A4E4D] mb-3 mt-6">2.2 Spa Listing Information</h3>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    For spa establishments listed on our platform, we collect:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70 mt-4">
                                    <li>Business name, address, and contact details</li>
                                    <li>Services offered (massages, treatments, wellness programs)</li>
                                    <li>Operating hours and pricing information</li>
                                    <li>Amenities and facilities available</li>
                                    <li>Photos and descriptions of spa premises</li>
                                    <li>Licenses and certifications</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">3. How We Use Your Information</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    We use the collected information to:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li>Provide and maintain our spa directory services</li>
                                    <li>Help you discover spas based on location, services, ratings, and preferences</li>
                                    <li>Process your inquiries and connect you with spa establishments</li>
                                    <li>Send appointment reminders and wellness recommendations</li>
                                    <li>Improve our platform with personalized spa suggestions</li>
                                    <li>Verify and maintain accurate spa listing information</li>
                                    <li>Prevent fraudulent listings and ensure service quality</li>
                                    <li>Comply with legal obligations in the wellness industry</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">4. Spa Booking & Appointments</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    When you contact a spa through our platform or express interest in services:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70 mt-4">
                                    <li>Your contact information may be shared with the specific spa you're inquiring about</li>
                                    <li>Appointment preferences and service requests are forwarded to the spa</li>
                                    <li>Spas are required to handle your data in accordance with applicable privacy laws</li>
                                    <li>We do not store sensitive health information or treatment details</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">5. Information Sharing</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    We may share your information with:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li><strong>Spa Partners:</strong> To facilitate bookings and inquiries you initiate</li>
                                    <li><strong>Service Providers:</strong> For website hosting, analytics, and customer support</li>
                                    <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
                                </ul>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mt-4">
                                    We never sell your personal information to third parties for marketing purposes.
                                </p>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">6. Data Security</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    We implement industry-standard security measures to protect your information:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70 mt-4">
                                    <li>SSL encryption for all data transmissions</li>
                                    <li>Secure servers with regular security audits</li>
                                    <li>Limited access to personal data on a need-to-know basis</li>
                                    <li>Regular backup and disaster recovery procedures</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">7. Your Rights</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed mb-4">
                                    You have the right to:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-[#4A4E4D]/70">
                                    <li>Access your personal data we hold</li>
                                    <li>Request correction of inaccurate information</li>
                                    <li>Delete your account and associated data</li>
                                    <li>Opt-out of marketing communications</li>
                                    <li>Request data portability</li>
                                    <li>Withdraw consent for data processing</li>
                                </ul>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">8. Cookies & Tracking</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    We use cookies and similar technologies to enhance your browsing experience, 
                                    remember your preferences (like favorite spas), analyze site traffic, and 
                                    personalize spa recommendations. You can control cookie settings through your browser.
                                </p>
                            </section>

                            <section className="bg-white rounded-2xl p-8 border border-[#E0DAD0]">
                                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">9. Contact Us</h2>
                                <p className="text-[#4A4E4D]/70 leading-relaxed">
                                    For privacy-related questions or to exercise your rights, contact us at:
                                </p>
                                <div className="mt-4 p-4 bg-[#F9F6F0] rounded-xl">
                                    <p className="text-[#4A4E4D]"><strong>SAPA - The Spa Directory</strong></p>
                                    <p className="text-[#4A4E4D]/70">Email: info@thesapa.com</p>
                                    <p className="text-[#4A4E4D]/70">Phone: +91xxxxxxxxxx</p>
                                    <p className="text-[#4A4E4D]/70">Bangalore, Karnataka, India</p>
                                </div>
                            </section>
                        </div>

                        <div className="mt-12 text-center">
                            <Link to="/" className="btn-outline-gold">
                                Back to Home
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default PrivacyPolicy;
